/**
 * Chips Component for Poker Reborn
 *
 * Reusable visual component that displays a pile/stack of chips.
 * Animates IN-PLACE only - does not fly across the screen.
 * Movement between locations uses separate "flying chips" instances.
 *
 * @see docs/screens/room/CHIPS.md - Full specification
 */

import { Container, Graphics, Sprite } from 'pixi.js';
import {
  DENOMINATIONS_DESC,
  calculateRealisticBreakdown,
  createChipSprite,
  getChipDenomination,
  type ChipDenomination,
} from './ChipColors';
import { getScaledValue } from '@/utils/responsive';

// =============================================================================
// TYPES
// =============================================================================

export type ChipSize = 'small' | 'medium' | 'large';

export interface ChipsOptions {
  /** Chip size preset */
  size?: ChipSize;
  /** Maximum chips to display visually in the main pile */
  maxChips?: number;
  /** Maximum chips per pile in row 2 (3 piles) */
  maxChipsRow2?: number;
  /** Maximum chips per pile in row 3 (6 piles) */
  maxChipsRow3?: number;
  /** Show value text below chips */
  showValue?: boolean;
}

export interface ChipBreakdown {
  [denomination: number]: number;
}

// =============================================================================
// BBS BREAKPOINTS
// =============================================================================

/** Chip diameter breakpoints per size preset (smooth interpolation) */
const DIAMETER_BREAKPOINTS: Record<ChipSize, Record<number, number>> = {
  small:  { 375: 24, 720: 30, 1440: 33, 1920: 36 },
  medium: { 375: 24, 720: 30, 1440: 33, 1920: 36 },
  large:  { 375: 40, 720: 50, 1440: 60, 1920: 60 },
};

/** Stack offset as fraction of chip diameter (gap between chips = diameter × ratio) */
const STACK_OFFSET_RATIO = 0.08;

const DEFAULT_OPTIONS: Required<ChipsOptions> = {
  size: 'medium',
  maxChips: 12,
  maxChipsRow2: 9,
  maxChipsRow3: 7,
  showValue: false,
};

// Animation constants
const PULSE_SCALE = 1.15;
const PULSE_DURATION = 150; // ms

/** Number of rotation angle variants in the chip atlas */
const ROTATION_ANGLE_COUNT = 5;

// =============================================================================
// SECONDARY PILE ROW CONFIGURATION
// =============================================================================

/** Configuration for a row of secondary piles behind the main pile */
interface SecondaryRowConfig {
  /** Scale relative to main pile diameter */
  scale: number;
  /** Max chips per pile in this row */
  maxChipsPerPile: number;
  /** Pile positions as fraction of main chip diameter */
  offsets: { x: number; y: number }[];
}

/**
 * Secondary row definitions (index 0 = row closest to main, index 1 = furthest back).
 *
 * Layout (top-down view):
 *   (3L)  (3IL) (3IR)  (3R)   ← row index 1: 4 piles
 *       (2L)      (2R)         ← row index 0: 2 piles
 *            (M)               ← main pile
 */
const SECONDARY_ROW_CONFIGS: readonly SecondaryRowConfig[] = [
  // Row 2: three piles behind main
  {
    scale: 0.67,
    maxChipsPerPile: 9,
    offsets: [
      { x: -0.55, y: -0.35 },
      { x: 0, y: -0.5 },
      { x: 0.55, y: -0.35 },
    ],
  },
  // Row 3: six piles behind row 2
  {
    scale: 0.67,
    maxChipsPerPile: 7,
      offsets: [
        { x: -0.28, y: -0.75 },   // inner left (furthest back)
        { x: 0.28, y: -0.75 },    // inner right (furthest back)
        { x: -0.8, y: -0.6 },   // middle left
        { x: 0.8, y: -0.6 },    // middle right
        { x: -1.08, y: -0.4 }, // outer left (closest)
        { x: 1.08, y: -0.4 },  // outer right (closest)
      ],
  },
];

// =============================================================================
// CHIPS COMPONENT
// =============================================================================

/**
 * Visual chip stack component.
 *
 * Key behaviors:
 * - Displays chip value as a visual stack
 * - Animates in-place on value change (pulse)
 * - Does NOT move - movement is handled externally
 */
export class Chips extends Container {
  private value = 0;
  private options: Required<ChipsOptions>;
  private isAnimating = false;
  private currentScreenWidth = 1920;

  /** Ordered chip stack (bottom to top) preserving physical pile order across redraws */
  private chipStack: { denominationValue: number; angleIndex: number }[] = [];

  /** Main pile container (rendered in front) */
  private mainPileContainer: Container;
  /** Secondary row containers: [row][pile] — furthest rows added first for z-order */
  private secondaryContainers: Container[][] = [];

  constructor(options: ChipsOptions = {}) {
    super();
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.label = 'chips';

    // Secondary rows render behind main pile (furthest row added first = behind)
    for (let rowIdx = SECONDARY_ROW_CONFIGS.length - 1; rowIdx >= 0; rowIdx--) {
      const rowConfig = SECONDARY_ROW_CONFIGS[rowIdx];
      if (!rowConfig) continue;

      const rowContainers: Container[] = [];
      for (let pileIdx = 0; pileIdx < rowConfig.offsets.length; pileIdx++) {
        const container = new Container();
        container.label = `chips-row${String(rowIdx + 2)}-pile${String(pileIdx)}`;
        container.visible = false;
        this.addChild(container);
        rowContainers.push(container);
      }
      this.secondaryContainers[rowIdx] = rowContainers;
    }

    // Main pile renders last (in front of all secondary rows)
    this.mainPileContainer = new Container();
    this.mainPileContainer.label = 'chips-main';
    this.addChild(this.mainPileContainer);
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /**
   * Get current chip value.
   */
  getValue(): number {
    return this.value;
  }

  /**
   * Set chip value and redraw.
   *
   * @param value - New chip value
   * @param animated - Whether to animate the change (default: true)
   */
  setValue(value: number, animated = true): void {
    const oldValue = this.value;
    this.value = Math.max(0, Math.floor(value));

    if (this.value !== oldValue) {
      this.redraw();

      if (animated && this.value > 0) {
        this.pulse();
      }
    }
  }

  /**
   * Add to current value.
   *
   * @param delta - Amount to add (can be negative)
   * @param animated - Whether to animate the change
   */
  addValue(delta: number, animated = true): void {
    this.setValue(this.value + delta, animated);
  }

  /**
   * Get chip breakdown by denomination.
   */
  getBreakdown(): ChipBreakdown {
    const breakdown: ChipBreakdown = {};
    const map = calculateRealisticBreakdown(this.value, this.getMaxCapacity());

    for (const [denom, count] of map) {
      breakdown[denom] = count;
    }

    return breakdown;
  }

  /**
   * Play pulse animation (scale up and back).
   */
  pulse(): void {
    if (this.isAnimating) return;
    this.isAnimating = true;

    const startScale = this.scale.x;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / PULSE_DURATION, 1);

      // Ease out then back: 0 -> 1 -> 0 curve
      const curve = Math.sin(progress * Math.PI);
      const scale = startScale + (PULSE_SCALE - startScale) * curve;

      this.scale.set(scale);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.scale.set(startScale);
        this.isAnimating = false;
      }
    };

    requestAnimationFrame(animate);
  }

  /**
   * Reset for object pooling.
   */
  reset(): void {
    this.value = 0;
    this.clearChips();
    this.chipStack = [];
    this.scale.set(1);
    this.alpha = 1;
    this.visible = true;
    this.isAnimating = false;
  }

  /**
   * Resize for different screen widths (BBS).
   * @param screenWidth - Current screen width in pixels
   */
  resize(screenWidth: number): void {
    if (screenWidth === this.currentScreenWidth) return;
    this.currentScreenWidth = screenWidth;
    if (this.value > 0) {
      this.redraw();
    }
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  /**
   * Clear all chip graphics from all pile containers.
   */
  private clearChips(): void {
    this.clearContainer(this.mainPileContainer);
    for (const rowContainers of this.secondaryContainers) {
      for (const container of rowContainers) {
        this.clearContainer(container);
      }
    }
  }

  /**
   * Remove and destroy all children of a container.
   */
  private clearContainer(container: Container): void {
    for (const child of [...container.children]) {
      container.removeChild(child);
      child.destroy();
    }
  }

  /**
   * Redraw chips based on current value.
   *
   * Maintains chipStack as an ordered physical pile (bottom to top):
   * - Existing chips keep their position and rotation angle
   * - When value increases, new chips are appended on TOP of the pile
   * - When value decreases, excess chips are removed
   * This prevents visual "shuffle" and ensures natural stacking.
   *
   * Secondary rows appear/disappear automatically: first the breakdown is
   * computed for the main pile only. If it overflows, row 2 is added and
   * the breakdown recomputed with higher capacity. Same for row 3.
   */
  private redraw(): void {
    this.clearChips();

    if (this.value <= 0) {
      this.chipStack = [];
      this.hideAllSecondaryRows();
      return;
    }

    const maxChips = this.options.maxChips;

    // Progressive fill: compute breakdown for main pile first, only expand
    // to the next row when the current capacity is full.
    let activeSecondaryRows = 0;
    let totalCapacity = maxChips;
    let breakdown = calculateRealisticBreakdown(this.value, totalCapacity);
    let totalChipCount = this.countBreakdownChips(breakdown);

    for (let rowIdx = 0; rowIdx < SECONDARY_ROW_CONFIGS.length; rowIdx++) {
      if (totalChipCount < totalCapacity) break; // still room — no expansion needed
      const rowConfig = SECONDARY_ROW_CONFIGS[rowIdx];
      if (!rowConfig) break;
      activeSecondaryRows = rowIdx + 1;
      totalCapacity += this.getRowMaxChipsPerPile(rowIdx) * rowConfig.offsets.length;
      breakdown = calculateRealisticBreakdown(this.value, totalCapacity);
      totalChipCount = this.countBreakdownChips(breakdown);
    }

    // Count how many of each denomination we need
    const needed = new Map<number, number>();
    for (const [denomValue, count] of breakdown) {
      needed.set(denomValue, count);
    }

    // Walk existing stack bottom-to-top, keeping chips still present in breakdown
    const keptStack: { denominationValue: number; angleIndex: number }[] = [];
    for (const chip of this.chipStack) {
      const remainingNeeded = needed.get(chip.denominationValue) ?? 0;
      if (remainingNeeded > 0 && keptStack.length < totalCapacity) {
        keptStack.push(chip);
        needed.set(chip.denominationValue, remainingNeeded - 1);
      }
    }

    // Append new chips on top (highest denomination first among new chips)
    for (const denom of DENOMINATIONS_DESC) {
      const remainingCount = needed.get(denom.value) ?? 0;
      for (let i = 0; i < remainingCount && keptStack.length < totalCapacity; i++) {
        keptStack.push({
          denominationValue: denom.value,
          angleIndex: Math.floor(Math.random() * ROTATION_ANGLE_COUNT),
        });
      }
    }

    this.chipStack = keptStack;

    const diameter = getScaledValue(this.currentScreenWidth, DIAMETER_BREAKPOINTS[this.options.size], { discrete: false });

    // Main pile: first maxChips from chipStack
    const mainChips = this.chipStack.slice(0, maxChips);
    this.renderPile(mainChips, this.mainPileContainer, diameter);

    // Distribute overflow across active secondary rows
    let overflowStart = maxChips;

    for (let rowIdx = 0; rowIdx < SECONDARY_ROW_CONFIGS.length; rowIdx++) {
      const rowConfig = SECONDARY_ROW_CONFIGS[rowIdx];
      const rowContainers = this.secondaryContainers[rowIdx];
      if (!rowConfig || !rowContainers) continue;

      if (rowIdx >= activeSecondaryRows) {
        // This row is not needed — hide all its containers
        for (const container of rowContainers) {
          container.visible = false;
        }
        continue;
      }

      const rowCapacity = this.getRowMaxChipsPerPile(rowIdx) * rowConfig.offsets.length;
      const rowChips = this.chipStack.slice(overflowStart, overflowStart + rowCapacity);
      overflowStart += rowCapacity;

      if (rowChips.length > 0) {
        const rowDiameter = diameter * rowConfig.scale;
        const maxPerPile = this.getRowMaxChipsPerPile(rowIdx);

        // Group same denomination together, then fill piles sequentially
        const sortedChips = [...rowChips].sort((a, b) => b.denominationValue - a.denominationValue);

        const pileChips: { denominationValue: number; angleIndex: number }[][] =
          rowConfig.offsets.map(() => []);

        let pileIdx = 0;
        for (const chip of sortedChips) {
          // Move to next pile if current is full
          while (pileIdx < pileChips.length && (pileChips[pileIdx]?.length ?? 0) >= maxPerPile) {
            pileIdx++;
          }
          if (pileIdx >= pileChips.length) break;
          pileChips[pileIdx]?.push(chip);
        }

        for (let pi = 0; pi < rowContainers.length; pi++) {
          const container = rowContainers[pi];
          const offset = rowConfig.offsets[pi];
          const chips = pileChips[pi];
          if (!container || !offset || !chips || chips.length === 0) {
            if (container) container.visible = false;
            continue;
          }

          container.visible = true;
          container.x = offset.x * diameter;
          container.y = offset.y * diameter;
          this.renderPile(chips, container, rowDiameter);
        }
      } else {
        // No overflow for this row — hide all its containers
        for (const container of rowContainers) {
          container.visible = false;
        }
      }
    }
  }

  /**
   * Get the max chips per pile for a secondary row, using options override if set.
   */
  private getRowMaxChipsPerPile(rowIdx: number): number {
    if (rowIdx === 0) return this.options.maxChipsRow2;
    if (rowIdx === 1) return this.options.maxChipsRow3;
    return SECONDARY_ROW_CONFIGS[rowIdx]?.maxChipsPerPile ?? 4;
  }

  /**
   * Calculate maximum chip capacity across main pile and all secondary rows.
   */
  private getMaxCapacity(): number {
    let capacity = this.options.maxChips;
    for (let rowIdx = 0; rowIdx < SECONDARY_ROW_CONFIGS.length; rowIdx++) {
      const rowConfig = SECONDARY_ROW_CONFIGS[rowIdx];
      if (rowConfig) {
        capacity += this.getRowMaxChipsPerPile(rowIdx) * rowConfig.offsets.length;
      }
    }
    return capacity;
  }

  /**
   * Count total chips in a breakdown map.
   */
  private countBreakdownChips(breakdown: Map<number, number>): number {
    let count = 0;
    for (const chipCount of breakdown.values()) {
      count += chipCount;
    }
    return count;
  }

  /**
   * Hide all secondary row containers.
   */
  private hideAllSecondaryRows(): void {
    for (const rowContainers of this.secondaryContainers) {
      for (const container of rowContainers) {
        container.visible = false;
      }
    }
  }

  /**
   * Render a pile of chips into a target container.
   */
  private renderPile(
    chips: { denominationValue: number; angleIndex: number }[],
    target: Container,
    diameter: number,
  ): void {
    const stackOffset = diameter * STACK_OFFSET_RATIO;

    let yOffset = 0;
    for (let i = 0; i < chips.length; i++) {
      const chip = chips[i];
      if (!chip) continue;

      const denom = getChipDenomination(chip.denominationValue);
      if (!denom) continue;

      const isBottomChip = i === 0;
      const isTopChip = i === chips.length - 1;
      const angleIndex = isTopChip ? 0 : chip.angleIndex;
      const chipDisplayObject = this.createChipDisplayObject(denom, diameter, isBottomChip, angleIndex);

      chipDisplayObject.y = -yOffset;
      chipDisplayObject.x = 0;

      target.addChild(chipDisplayObject);
      yOffset += stackOffset;
    }
  }

  /**
   * Create a single chip display object (sprite or graphics fallback).
   */
  private createChipDisplayObject(denom: ChipDenomination, diameter: number, withShadow = false, angleIndex?: number): Sprite | Graphics {
    // Try to create a sprite first (bottom chip gets drop shadow variant)
    const sprite = createChipSprite(denom.value, angleIndex, withShadow);
    if (sprite) {
      // Scale sprite to match desired diameter
      // Atlas sprites are 256×256 pixels
      const scale = diameter / 256;
      sprite.scale.set(scale);
      return sprite;
    }

    // Fallback to graphics
    return this.createChipGraphic(denom, diameter);
  }

  /**
   * Create a single chip graphic (fallback when sprite not available).
   */
  private createChipGraphic(denom: ChipDenomination, diameter: number): Graphics {
    const graphics = new Graphics();
    const radius = diameter / 2;

    // Shadow (slightly offset)
    graphics.circle(1.5, 1.5, radius);
    graphics.fill({ color: 0x000000, alpha: 0.3 });

    // Main chip body
    graphics.circle(0, 0, radius);
    graphics.fill(denom.color);

    // Edge stripe pattern (4 stripes)
    const stripeWidth = radius * 0.15;
    const stripeOffset = radius * 0.7;

    for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 2) {
      const x = Math.cos(angle) * stripeOffset;
      const y = Math.sin(angle) * stripeOffset;

      graphics.circle(x, y, stripeWidth);
      graphics.fill(denom.accent);
    }

    // Center circle (value area)
    const centerRadius = radius * 0.5;
    graphics.circle(0, 0, centerRadius);
    graphics.fill(denom.color);

    // Border
    graphics.circle(0, 0, radius);
    graphics.stroke({ width: 1, color: denom.border });

    // Highlight (top-left shine)
    graphics.circle(-radius * 0.25, -radius * 0.25, radius * 0.15);
    graphics.fill({ color: 0xffffff, alpha: 0.4 });

    return graphics;
  }
}
